import { Formik } from 'formik';
import { GraphQLError } from 'graphql';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Box } from 'reflexbox';
import * as yup from 'yup';

import * as graphql from '@treams/treams-network';
import {
	Button,
	Callout,
	FlexGap,
	GraphQLErrorMessage,
	Input,
	Label,
	Paragraph,
	Subheading,
	UnderlineLink,
	tokens,
} from '@treams/treams-ui';

import BareLayout from '../../../../../../src/packages/kakapo-ui-application/layouts/BareLayout';
import PublicForm from '../../components/authentication/PublicForm';

export default function ForgotPassword() {
	const { t } = useTranslation();

	const [commit] = graphql.useForgotPasswordMutation();

	const [success, setSuccess] = useState(false);
	const [error, setError] = useState<GraphQLError | undefined>(undefined);

	const validationSchema = yup.object({
		login: yup.string().trim().max(128).required(t('common.requestNewPassword.loginRequired')),
	});

	return (
		<BareLayout>
			<Formik
				initialValues={{
					login: '',
				}}
				validationSchema={validationSchema}
				onSubmit={async ({ login }) => {
					try {
						await commit({
							variables: { input: { username: login.trim() } },
						});
						setSuccess(true);
					} catch (errors) {
						setError(errors.graphQLErrors[0]);
					}
				}}
			>
				{({ errors, touched }) => (
					<PublicForm>
						{success ? (
							<>
								<Callout
									mb={`${tokens.gap.group}px`}
									title={t('common.requestNewPassword.success.title')}
									description={t('common.requestNewPassword.success.message')}
									color="employee"
								/>
								<UnderlineLink as={Link} to="/" color="secondary" small>
									{t('common.homePage')}
								</UnderlineLink>
							</>
						) : (
							<>
								<Subheading mb={`${tokens.gap.group}px`}>
									{t('common.requestNewPassword.title')}
								</Subheading>
								<Paragraph mb={`${tokens.gap.section}px`}>
									{t('common.requestNewPassword.content')}
								</Paragraph>
							</>
						)}

						<GraphQLErrorMessage error={error} />

						{success ? null : (
							<>
								<Box mb={3}>
									<Label mb={2}>{t('common.userName')}:</Label>
									<Input.Formik name="login" autoFocus />
									{touched.login && errors.login && (
										<Callout mt={2} color="red">
											{errors.login}
										</Callout>
									)}
								</Box>
								<FlexGap flexDirection="column" gap={tokens.gap.group} alignItems="flex-start">
									<Button.Formik title={t('common.requestNewPassword.button')} />
									<UnderlineLink as={Link} to="/" color="secondary" small>
										{t('common.homePage')}
									</UnderlineLink>
								</FlexGap>
							</>
						)}
					</PublicForm>
				)}
			</Formik>
		</BareLayout>
	);
}
